<script setup lang="ts">
import {computed, onMounted, PropType, ref, watch} from 'vue';
import {
    AvailabilityState,
    CabPeriod,
    CabReportItem,
    OnderwerpEnum_name,
    PeriodEnum,
    renderCabPeriod,
    renderTerseDate,
    StatusEnum,
} from 'common';
import {ElButton, ElDialog, ElMessage} from 'element-plus';
import {
    BackendError,
    listAllReporterReportItems,
    listReporterReportItems,
    listObjects,
    updateReportItem,
    updateReportItemAsAdmin,
} from '@/base/callBackend.js';
import {done, showHideLoading} from '@/base/loadingState.js';
import {ReportGroup} from '@/base/utils.js';
import ReportItemFilters from '@/views/shared/ReportItemFilters.vue';
import ReportItemStrip from '@/views/shared/ReportItemStrip.vue';
import KpiIcon from '@/views/icons/KpiIcon.vue';
import ExprIcons from '@/views/shared/ExprIcons.vue';
import WhiteCircleIcon from '@/views/icons/WhiteCircleIcon.vue';
import GreenCircleIcon from '@/views/icons/GreenCircleIcon.vue';
import OrangeCircleIcon from '@/views/icons/OrangeCircleIcon.vue';
import BlueCircleIcon from '@/views/icons/BlueCircleIcon.vue';
import PurpleCircleIcon from '@/views/icons/PurpleCircleIcon.vue';
import RedCircleIcon from '@/views/icons/RedCircleIcon.vue';
import {stateForReporters} from '@/base/TMP_globalState.js';
import {numHedenReady, numHedenTodo} from '@/base/reporter.js';
import VerklaringDerSymbolen from '@/views/shared/VerklaringDerSymbolen.vue';

const props = defineProps({
    reportGroup: {
        type    : String as PropType<ReportGroup>,
        required: false,
    },
    isAdmin: {
        type   : Boolean,
        default: false,
    },
});

//============================================================================================================================================
const storageKeyPrefix = computed(() => props.isAdmin ? 'admin' : (props.reportGroup ?? 'reporter'));
const allRaw           = ref<CabReportItem[]>([]); // Raw data from backend (all statuses)
const all              = ref<CabReportItem[]>([]); // after the status filter
const allNaOnderwerpen = ref<CabReportItem[]>([]); // after all filters
//
const numReadyAll       = computed<number>(() => allNaOnderwerpen.value.filter(ri => ri.isSubmittable()).length);
const numTodoAll        = computed<number>(() => allNaOnderwerpen.value.filter(ri => !ri.isSubmittable()).length);
const numReadyTotal     = computed<number>(() => allRaw.value.filter(ri => ri.isSubmittable()).length);
const numTodoTotal      = computed<number>(() => allRaw.value.filter(ri => !ri.isSubmittable()).length);

watch([numReadyTotal, numTodoTotal], () => {
    if (!props.isAdmin && props.reportGroup === ReportGroup.heden) {
        numHedenReady.value = numReadyTotal.value;
        numHedenTodo.value  = numTodoTotal.value;
    }
}, {immediate: true});

//
const editIndex                 = ref<number>();
const cursorLine                = ref<number>();
const editReportItem            = computed<CabReportItem | undefined>(() => editIndex.value != undefined ? allNaOnderwerpen.value[editIndex.value] : undefined);
//
const isNotHeden                = ref<boolean>(!props.isAdmin && !!props.reportGroup && props.reportGroup !== ReportGroup.heden);
const dialogVisible             = ref<boolean>();
const indienenInProgress        = ref<boolean>(false);
const viewLoading               = ref<boolean>(true);

onMounted(async () => {
    if (!props.isAdmin && stateForReporters.value === AvailabilityState.closed) {
        return;
    }
    showHideLoading(true);
    viewLoading.value = true;
    await getAllFromBackend();
    viewLoading.value = false;
    showHideLoading(false);
});

async function getAllFromBackend() {
    try {
        showHideLoading(true);
        let cabReportItems: CabReportItem[];
        if (props.isAdmin) {
            // Fetch ALL statuses, filter client-side for instant status filter changes
            cabReportItems = await listObjects(CabReportItem);
        } else if (props.reportGroup) {
            cabReportItems = await listReporterReportItems(props.reportGroup);
        } else {
            // Fetch ALL statuses, filter client-side for instant status filter changes
            cabReportItems = await listAllReporterReportItems();
        }
        allRaw.value = cabReportItems.sort((a, b) => a.compare(b));
    } catch (e) {
        console.error(e);
        if (e instanceof BackendError) {
            ElMessage.error(`Error: ${e.details}`);
        }
    } finally {
        showHideLoading(false);
    }
}

//============================================================================================================================================
async function onIndienen() {
    indienenInProgress.value = true;
    try {
        const submittableItems = allNaOnderwerpen.value.filter(ri => ri.isSubmittable());
        const updateFn         = props.isAdmin ? updateReportItemAsAdmin : updateReportItem;

        // Update status locally and prepare update promises
        const updatePromises = submittableItems.map(ri => {
            ri.status = StatusEnum.submitted;
            return updateFn(ri);
        });

        // Execute all updates in parallel
        await Promise.all(updatePromises);

        // Refresh data from backend instead of full page reload
        await getAllFromBackend();
    } catch (e) {
        console.error('Error submitting items:', e);
        if (e instanceof BackendError) {
            ElMessage.error(`Error: ${e.details}`);
        }
    } finally {
        indienenInProgress.value = false;
    }
}

//============================================================================================================================================
function renderIndienMoment(ri: CabReportItem) {
    let period: CabPeriod;
    if (ri.isMijlpaal) {
        period = CabPeriod.of(ri.meetMoment!, PeriodEnum.Q).previousPeriod();
    } else {
        period = ri.period[0].qPeriod();
    }
    return renderCabPeriod(period);
}

function dynLineClass(i: number): string {
    const onEvenLine   = i % 2 == 0;
    const onCursorLine = i == cursorLine.value;
    return onEvenLine ? onCursorLine ? 'bg-green-300' : 'bg-green-200' : onCursorLine ? 'bg-green-300' : 'bg-green-100';
}

function getStatusIcon(ri: CabReportItem) {
    switch (ri.status) {
        case StatusEnum.future:
            return WhiteCircleIcon;
        case StatusEnum.open:
        case StatusEnum.overdue:
            return ri.isSubmittable() ? GreenCircleIcon : OrangeCircleIcon;
        case StatusEnum.submitted:
            return BlueCircleIcon;
        case StatusEnum.accepted:
            return PurpleCircleIcon;
        case StatusEnum.rejected:
            return RedCircleIcon;
    }
}

</script>

<template>
    <div class="w-full h-full relative">

        <div class="pt-5">
            <template v-if="(isAdmin || stateForReporters!==AvailabilityState.closed) && !viewLoading">
                <ReportItemFilters
                    :all="allRaw"
                    :storage-key-prefix="storageKeyPrefix"
                    :show-reporter-filter="isAdmin"
                    :show-status-filter="isAdmin || !reportGroup"
                    :is-not-heden="isNotHeden"
                    @update:status-filtered="all = $event"
                    @update:filtered="allNaOnderwerpen = $event"
                />
            </template>
        </div>

        <div class="flex py-3 px-10">
            <!-- Content -->
            <div class="w-full border-8 border-white" v-if="done && !viewLoading">
                <!-- no overflow-x-auto here: an overflow container would trap the sticky column headers -->
                <div>
                    <div v-if="all.length===0"
                         class="font-thin text-gray-500 text-xl text-center m-10 h-72"
                    >
                        - er staan op dit moment geen items open -
                    </div>
                    <div v-else-if="allNaOnderwerpen.length===0"
                         class="font-thin text-gray-500 text-xl text-center m-10 h-72"
                    >
                        - lege selectie -
                    </div>
                    <table class="table-auto border-collapse border w-full cursor-pointer">
                        <thead v-if="allNaOnderwerpen.length">
                        <tr class="text-xs font-normal text-gray-500 [&>th]:sticky [&>th]:top-[var(--mainheader-height,3.5rem)] [&>th]:z-10 [&>th]:bg-white">
                            <th class="py-1 px-2"/>
                            <th class="whitespace-nowrap text-center font-normal py-1 px-3">kwartaal</th>
                            <th class="whitespace-nowrap text-center font-normal py-1 px-3">instelling</th>
                            <th class="whitespace-nowrap text-center font-normal py-1 px-3">thema</th>
                            <th class="whitespace-nowrap text-center font-normal py-1 px-3">onderwerp</th>
                            <th class="py-1 pl-3"/>
                            <th class="whitespace-nowrap text-left font-normal py-1 pl-0 pr-3">KPI</th>
                            <th class="py-1 pl-0 pr-3"/>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(ri,i) in allNaOnderwerpen"
                            :key="ri.uuid"
                            class=""
                            :class="dynLineClass(i)"
                            @mouseenter="cursorLine = i"
                            @mouseleave="cursorLine = -1"
                            @click="editIndex = i; dialogVisible = true"
                        >
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-2">
                                <component :is="getStatusIcon(ri)"/>
                            </td>
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-3">
                                {{ renderIndienMoment(ri) }}
                            </td>
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-3">
                                {{ ri.organization[0].name }}
                            </td>
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-3">
                                {{ ri.theme[0].name }}
                            </td>
                            <td class="whitespace-nowrap text-center border-y border-gray-300 py-2 px-3">
                                {{ OnderwerpEnum_name(ri.kpi[0].onderwerp) }}
                            </td>
                            <td class="                  text-left   border-y border-gray-300 py-2 pl-3">
                                <KpiIcon :kpi="ri.kpi[0]"/>
                            </td>
                            <td class="                  text-left   border-y border-gray-300 py-2 pl-0 pr-3 font-bold w-full">
                                {{ ri.kpi[0].name }}
                            </td>
                            <td class="                  text-left   border-y border-gray-300 py-2 pl-0 pr-3 font-bold w-full">
                                <ExprIcons :kpi="ri.kpi[0]"/>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="flex flex-row pb-2 pl-10 cursor-pointer"
             v-if="!isAdmin && stateForReporters===AvailabilityState.write && all.length!=0">
            <el-button type="primary"
                       @click="onIndienen()"
                       :disabled="indienenInProgress || numReadyAll==0"
                       v-if="numReadyAll"
                       class="w-40"
            >
                {{ numReadyAll == 1 ? '1 Item Indienen' : `${numReadyAll} Items Indienen` }}
            </el-button>
            <div class="ml-2 mt-1 flex-row flex" v-if="numReadyAll!=0">
                Er {{ numReadyAll == 1 ? `staat 1 item` : `staan ${numReadyAll} items` }}
                <GreenCircleIcon class="mx-1"/>
                klaar om ingediend te worden.
            </div>
            <div class="ml-2 mt-1 flex-row flex" v-if="numTodoAll!=0">
                Er {{ numTodoAll == 1 ? `kan nog 1 item` : `kunnen nog ${numTodoAll} items` }}
                <OrangeCircleIcon class="mx-1"/>
                ingevuld worden.
            </div>
        </div>

        <VerklaringDerSymbolen/>

    </div>

    <el-dialog
        v-model="dialogVisible"
        width="90%"
        align-center
        destroy-on-close
        :show-close="false"
        v-if="editReportItem" :draggable="true"
    >
        <template #header>
            <div class="flex flex-row items-center text-lg">
                <div class="cursor-default mx-2 whitespace-nowrap">
                    <component :is="getStatusIcon(editReportItem)"/>
                </div>
                <div class="mx-2 whitespace-nowrap">
                    {{ editReportItem.organization[0].name }}
                </div>
                <div class="mx-2 whitespace-nowrap">
                    {{ editReportItem.theme[0].name }}
                </div>
                <div class="mx-2 whitespace-nowrap">
                    {{ OnderwerpEnum_name(editReportItem.kpi[0].onderwerp) }}
                </div>
                <KpiIcon :kpi="editReportItem.kpi[0]" class="ml-2"/>
                <div class="font-bold flex-wrap">
                    {{ editReportItem.kpi[0].name }}
                </div>
                <div class="flex-1">
                    <!--filler-->
                </div>

                <div class="mx-2 px-4 bg-gray-200 whitespace-nowrap">
                    {{ editReportItem.isMijlpaal ? `Mijlpaal per ${renderTerseDate(editReportItem.meetMoment)}` : `KPI over ${renderCabPeriod(editReportItem.period[0])}`
                    }}
                </div>
            </div>
        </template>

        <ReportItemStrip
            :flag-omissions="true"
            :item="editReportItem"
            :is-admin="isAdmin"
            @refresh-reports="getAllFromBackend"
        />

    </el-dialog>
</template>

<style>
/*noinspection CssUnusedSymbol*/
.el-button:disabled {
    @apply bg-gray-400 text-gray-200;
}
</style>
